'use client';

import { useState } from 'react';
import { StructuredBrief } from '@/lib/schemas';
import BriefQuiz from './BriefQuiz';

const EXAMPLES = [
  'Family of 4, 3-bed house in Bristol, budget £400k, good primary schools',
  '2-bed flat in Manchester, under £250k, commute under 30 min to Deansgate',
  'First-time buyer, 1-bed, Edinburgh, max £200k, near a park',
];

interface Props {
  onSearchStart: (sessionId: string) => void;
  searchComplete?: boolean;
  onNewSearch?: () => void;
}

export default function BriefForm({ onSearchStart, searchComplete, onNewSearch }: Props) {
  const [brief, setBrief]           = useState('');
  const [isLoading, setIsLoading]   = useState(false);
  const [parsedBrief, setParsedBrief] = useState<StructuredBrief | null>(null);
  const [sessionId, setSessionId]   = useState<string | null>(null);
  const [error, setError]           = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!brief.trim()) return;
    setIsLoading(true);
    setError(null);

    try {
      const res = await fetch('/api/brief', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ brief: brief }),
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || 'Something went wrong. Please try again.');
      }
      const data = await res.json();

      if (data.reiteration) {
        setBrief(data.reiteration);
      }

      if (data.structured_brief) {
        setParsedBrief(data.structured_brief);
      } else {
        throw new Error('Could not parse property criteria. Please try again with more details.');
      }

      if (data.session_id) {
        setSessionId(data.session_id);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartSearch = async (updatedBrief: StructuredBrief) => {
    if (!sessionId) return;
    setError(null);

    try {
      const res = await fetch(`/api/search/${sessionId}/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updated_brief: updatedBrief }),
      });
      if (!res.ok) throw new Error();
      onSearchStart(sessionId);
    } catch {
      setError('Failed to start search. Please try again.');
    }
  };

  const handleBack = () => {
    setParsedBrief(null);
    setSessionId(null);
    setError(null);
  };

  // After parsing — run the quiz to collect missing fields then start search
  if (parsedBrief && sessionId) {
    return (
      <div className="flex flex-col gap-2">
        <BriefQuiz
          initialBrief={parsedBrief}
          onComplete={handleStartSearch}
          searchComplete={searchComplete}
          onNewSearch={() => {
            setBrief('');
            setParsedBrief(null);
            setSessionId(null);
            setError(null);
            onNewSearch?.();
          }}
        />
        {error && <p className="text-xs text-red-500 px-1">{error}</p>}
        <button
          onClick={handleBack}
          className="px-1 text-xs text-[#7a7870] transition hover:text-[#0f0f0d]"
        >
          ← Edit brief
        </button>
      </div>
    );
  }

  // Initial search form
  return (
    <div className="pointer-events-auto w-full max-w-sm overflow-hidden rounded-2xl border border-black/10 bg-white/95 backdrop-blur">

      {/* Brand header */}
      <div className="px-6 pt-6 pb-4">
        <div className="flex items-center gap-2 mb-4">
          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#1a4a3a]">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
          </div>
          <span className="text-xs font-semibold tracking-widest text-[#7a7870] uppercase">Smart Property Search</span>
        </div>
        <h1 className="font-serif text-3xl font-semibold leading-snug text-[#0f0f0d]">
          Find your perfect<br />property in the UK
        </h1>
        <p className="mt-1.5 text-sm text-[#7a7870]">Describe what you&apos;re looking for in plain English</p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="px-6 pb-4 flex flex-col gap-3">
        <textarea
          value={brief}
          onChange={(e) => setBrief(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              if (brief.trim() && !isLoading) handleSubmit(e);
            }
          }}
          placeholder="e.g. 3-bed house near Bristol, budget £400k, good schools, near a park, 40 min commute to city centre…"
          rows={4}
          className="w-full resize-none rounded-xl border border-black/10 bg-[#faf8f4] px-4 py-3 text-sm text-[#0f0f0d] placeholder:text-[#b8b5ae] outline-none transition focus:border-[#2d7a5f] focus:bg-white focus:ring-2 focus:ring-[#d4ede5]"
        />
        {error && <p className="text-xs text-red-500">{error}</p>}
        <button
          type="submit"
          disabled={!brief.trim() || isLoading}
          className="flex items-center justify-center gap-2 rounded-xl bg-[#1a4a3a] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#2d7a5f] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-40"
        >
          {isLoading ? (
            <>
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
              Analysing…
            </>
          ) : (
            <>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
              </svg>
              Find Properties
            </>
          )}
        </button>
      </form>

      {/* Examples */}
      <div className="border-t border-black/10 px-6 py-4">
        <p className="mb-2.5 text-[11px] font-semibold uppercase tracking-widest text-[#b8b5ae]">Try an example</p>
        <div className="flex flex-col gap-1.5">
          {EXAMPLES.map((ex) => (
            <button
              key={ex}
              type="button"
              onClick={() => setBrief(ex)}
              className="rounded-lg px-3 py-2 text-left text-xs text-[#7a7870] transition hover:bg-[#f3f0ea] hover:text-[#1a4a3a]"
            >
              {ex}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
